import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  getAssignedQueries,
  saveQueryOutcome,
} from "../../../store/actions/centerActions/centerExtras";
import { exportToCSV } from "../../../utils/exportCsv";
import Paging from "../../Common/Paging";

/*
  Queries the council has passed to this center.

  The enquiry itself is the council's and cannot be edited here. What the
  center adds is the outcome: admission taken on a date, or not taken and
  why. The list is filtered on the server by the center in the login token,
  so nothing on this screen can widen it.
*/
const dmy = (value) => (value ? moment(value).format("DD/MM/YYYY") : "--");

/*
  1 is Affiliation and 2 is Admission - the order the contact form and
  ContactUsController have always used. This screen had them the other way
  round, so every admission enquiry was labelled Affiliation.
*/
const ENQUIRY_TYPE = { 1: "Affiliation", 2: "Admission" };

const OUTCOME_LABEL = {
  admitted: "Admission taken",
  not_admitted: "Not taken",
};

const AssignedQueries = () => {
  const dispatch = useDispatch();

  const [source, setSource] = useState("enquiry");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [list, setList] = useState(null);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);

  // The row whose outcome is being filled in.
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await dispatch(getAssignedQueries({ source, search, page }));
    if (res) {
      setList(res.data);
      setCounts(res.counts || {});
    }
    setLoading(false);
  }, [dispatch, source, search, page]);

  useEffect(() => {
    load();
  }, [load]);

  const pagination = list
    ? {
        page: Number(list.current_page),
        pages: Number(list.last_page),
        total: Number(list.total),
        per_page: Number(list.per_page),
      }
    : null;

  const rows = list?.data || [];
  const isEnquiry = source === "enquiry";

  /* The whole list, not only the page on screen. */
  const download = async () => {
    setBusy(true);
    const res = await dispatch(
      getAssignedQueries({ source, search, page: 1, per_page: 2000 })
    );
    setBusy(false);

    const all = res?.data?.data || [];
    if (all.length === 0) return;

    exportToCSV(
      all.map((row, i) =>
        isEnquiry
          ? {
              "Sr No.": i + 1,
              Name: row.name || "",
              Mobile: row.mobile || "",
              Email: row.email || "",
              Type: ENQUIRY_TYPE[Number(row.enquiry_type)] || "",
              Course: row.course_name || row.trade_name || "",
              City: row.city || "",
              State: row.state || "",
              Address: row.address || "",
              Message: row.message || "",
              Received: dmy(row.created_at),
              Assigned: dmy(row.assigned_at),
              Status: OUTCOME_LABEL[row.outcome] || "Pending",
              "Admission date": dmy(row.admission_date),
              Reason: row.reason || "",
            }
          : {
              "Sr No.": i + 1,
              "Complaint No.": row.complain_number || "",
              Student: row.student_name || "",
              "Roll No.": row.roll_number || "",
              Title: row.title || "",
              Description: row.description || "",
              Received: dmy(row.created_at),
              Assigned: dmy(row.assigned_at),
              Status: OUTCOME_LABEL[row.outcome] || "Pending",
              "Admission date": dmy(row.admission_date),
              Reason: row.reason || "",
            }
      ),
      `assigned-${source}-${moment().format("YYYY-MM-DD")}`
    );
  };

  const submitOutcome = async (e) => {
    e.preventDefault();
    setBusy(true);
    const done = await dispatch(
      saveQueryOutcome({
        assignment_id: form.assignment_id,
        outcome: form.outcome,
        admission_date: form.admission_date,
        reason: form.reason,
      })
    );
    setBusy(false);

    if (done) {
      setForm(null);
      load();
    }
  };

  const status = (row) => {
    if (row.outcome === "admitted") {
      return (
        <span class="text-success">
          Admission taken
          <br />
          <small>{dmy(row.admission_date)}</small>
        </span>
      );
    }
    if (row.outcome === "not_admitted") {
      return <span class="text-danger">Not taken</span>;
    }
    return <span class="text-muted">Pending</span>;
  };

  return (
    <div class="Title-box">
      <div class="row">
        <div class="col-md-6">
          <div class="BigTitle">Assigned Queries</div>
        </div>
        <div class="col-md-6 text-right">
          <button
            type="button"
            class="btn btn-comman"
            disabled={busy || rows.length === 0}
            onClick={download}
          >
            {busy ? "Working..." : "Download list"}
          </button>
        </div>
      </div>

      <div class="mid-content-box mt-3">
        <div class="row mb-2">
          <div class="col-md-4 mb-2">
            <input
              type="text"
              class="form-control"
              placeholder="Search by name, mobile or city"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div class="col-md-4 mb-2">
            <select
              class="form-control"
              value={source}
              onChange={(e) => {
                setSource(e.target.value);
                setPage(1);
              }}
            >
              <option value="enquiry">
                Website Enquiries ({counts.enquiry || 0})
              </option>
              <option value="query">
                Help Desk Queries ({counts.query || 0})
              </option>
            </select>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table">
            <thead>
              <tr>
                <th>Sr No.</th>
                {isEnquiry ? (
                  <>
                    <th>Name</th>
                    <th>Mobile</th>
                    <th>Email</th>
                    <th>Type</th>
                    <th>City / State</th>
                    <th>Course</th>
                  </>
                ) : (
                  <>
                    <th>Complaint No.</th>
                    <th>Student</th>
                    <th>Roll No.</th>
                    <th>Title</th>
                    <th>Course</th>
                    <th>Subject</th>
                  </>
                )}
                <th>Received</th>
                <th>Assigned</th>
                <th>Admission</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={12}>Loading...</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={12}>
                    Nothing has been assigned to your center yet.
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => (
                  <tr key={row.id}>
                    <td>
                      {(pagination.page - 1) * pagination.per_page + i + 1}
                    </td>
                    {isEnquiry ? (
                      <>
                        <td>{row.name || "--"}</td>
                        <td>{row.mobile || "--"}</td>
                        <td>{row.email || "--"}</td>
                        <td>
                          {ENQUIRY_TYPE[Number(row.enquiry_type)] || "--"}
                        </td>
                        <td>
                          {[row.city, row.state].filter(Boolean).join(", ") ||
                            "--"}
                        </td>
                        {/* the name, not the id the enquiry stores */}
                        <td>{row.course_name || row.trade_name || "--"}</td>
                      </>
                    ) : (
                      <>
                        <td>{row.complain_number || "--"}</td>
                        <td>{row.student_name || "--"}</td>
                        <td>{row.roll_number || "--"}</td>
                        <td>{row.title || "--"}</td>
                        <td>{row.course || "--"}</td>
                        <td>{row.subject || "--"}</td>
                      </>
                    )}
                    <td>{dmy(row.created_at)}</td>
                    <td>{dmy(row.assigned_at)}</td>
                    <td>
                      <div class="mb-1">{status(row)}</div>
                      <button
                        type="button"
                        class="btn btn-sm btn-light-primary"
                        onClick={() =>
                          setForm({
                            assignment_id: row.assignment_id,
                            name: row.name || row.student_name || "",
                            outcome: row.outcome || "admitted",
                            admission_date: row.admission_date || "",
                            reason: row.reason || "",
                          })
                        }
                      >
                        {row.outcome ? "Change" : "Update"}
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        class="btn btn-sm btn-light-primary"
                        onClick={() => setOpen(open === row.id ? null : row.id)}
                      >
                        {open === row.id ? "Hide" : "View"}
                      </button>
                    </td>
                  </tr>
                ))
              )}

              {/* the message, which is too long for a column */}
              {rows.map((row) =>
                open === row.id ? (
                  <tr key={`d-${row.id}`}>
                    <td colSpan={12} class="bg-light">
                      <b>{isEnquiry ? "Message" : "Description"}:</b>{" "}
                      {(isEnquiry ? row.message : row.description) ||
                        "Nothing was written."}
                      {isEnquiry && row.address ? (
                        <div class="mt-1">
                          <b>Address:</b> {row.address}
                        </div>
                      ) : null}
                      {row.outcome === "not_admitted" && row.reason ? (
                        <div class="mt-1 text-danger">
                          <b>Admission not taken:</b> {row.reason}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ) : null
              )}
            </tbody>
          </table>
        </div>

        <Paging pagination={pagination} onPage={setPage} />
      </div>

      {/* ---- what came of this enquiry ---- */}
      <Modal show={!!form} onHide={() => setForm(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Admission status{form?.name ? ` - ${form.name}` : ""}</Modal.Title>
        </Modal.Header>
        {form && (
          <form onSubmit={submitOutcome}>
            <Modal.Body>
              <div class="mb-2">
                <label>What happened?</label>
                <select
                  class="form-control"
                  value={form.outcome}
                  onChange={(e) => setForm({ ...form, outcome: e.target.value })}
                >
                  <option value="admitted">Admission taken</option>
                  <option value="not_admitted">Admission not taken</option>
                  <option value="">Not decided yet</option>
                </select>
              </div>

              {form.outcome === "admitted" && (
                <div class="mb-2">
                  <label>Admission date *</label>
                  <input
                    type="date"
                    class="form-control"
                    value={form.admission_date || ""}
                    onChange={(e) =>
                      setForm({ ...form, admission_date: e.target.value })
                    }
                    required
                  />
                </div>
              )}

              {form.outcome === "not_admitted" && (
                <div class="mb-2">
                  <label>Why was the admission not taken? *</label>
                  <textarea
                    class="form-control"
                    rows={3}
                    value={form.reason || ""}
                    onChange={(e) =>
                      setForm({ ...form, reason: e.target.value })
                    }
                    required
                  />
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <button
                type="button"
                class="btn btn-secondary"
                onClick={() => setForm(null)}
              >
                Cancel
              </button>
              <button type="submit" class="btn btn-comman" disabled={busy}>
                {busy ? "Saving..." : "Save"}
              </button>
            </Modal.Footer>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default AssignedQueries;
